// Fehler der Fachschicht: fester Code + fertiger deutscher Satz für den Nutzer.
// Server wirft AppError, der Dispatcher bildet ihn auf { error: { code, message, details } } ab,
// der Client baut daraus wieder einen AppError (fromErrorPayload).

export const ERROR_CODES = [
  'VALIDATION',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'BUSINESS_RULE',
  'LIMIT_EXCEEDED',
  'LOCK_TIMEOUT',
  'FEATURE_DISABLED',
  'CONFIRMATION_REQUIRED',
  'INTERNAL',
] as const

export type ErrorCode = (typeof ERROR_CODES)[number]

/** Standardtexte (Sie-Form). Fachcode übergibt nach Möglichkeit einen genaueren Satz. */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  VALIDATION: 'Die Eingabe ist nicht gültig. Bitte prüfen Sie die markierten Felder.',
  UNAUTHENTICATED: 'Bitte melden Sie sich an.',
  FORBIDDEN: 'Dafür fehlt Ihnen die Berechtigung.',
  NOT_FOUND: 'Der Datensatz wurde nicht gefunden.',
  CONFLICT:
    'Der Datensatz wurde zwischenzeitlich geändert. Bitte laden Sie die Ansicht neu und versuchen Sie es erneut.',
  BUSINESS_RULE: 'Der Vorgang ist so nicht möglich.',
  LIMIT_EXCEEDED: 'Der Vorgang überschreitet eine für Sie festgelegte Grenze.',
  LOCK_TIMEOUT:
    'Der Datensatz wird gerade von einem anderen Vorgang bearbeitet. Bitte versuchen Sie es gleich noch einmal.',
  FEATURE_DISABLED: 'Diese Funktion ist für Ihren Betrieb nicht aktiviert.',
  CONFIRMATION_REQUIRED: 'Dieser Vorgang muss vor der Ausführung bestätigt werden.',
  INTERNAL: 'Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut.',
}

export const ERROR_HTTP_STATUS: Record<ErrorCode, number> = {
  VALIDATION: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  BUSINESS_RULE: 422,
  LIMIT_EXCEEDED: 422,
  LOCK_TIMEOUT: 423,
  FEATURE_DISABLED: 403,
  CONFIRMATION_REQUIRED: 428,
  INTERNAL: 500,
}

/** Ein Feldfehler; `path` in Punkt-Schreibweise (`lines.2.qty`), leer = ganze Eingabe. */
export interface FieldIssue {
  path: string
  message: string
}

export interface ErrorDetails {
  /** Feldfehler bei VALIDATION */
  fields?: FieldIssue[]
  /** Name des verletzten Datenbank-Constraints (BUSINESS_RULE, CONFLICT) */
  constraint?: string
  /** betroffener Datensatz */
  entity?: { type: string; id?: string }
  [key: string]: unknown
}

/** Übertragungsform eines Fehlers (HTTP, Assistent). */
export interface ErrorPayload {
  code: ErrorCode
  message: string
  details?: ErrorDetails
}

export class AppError extends Error {
  readonly code: ErrorCode
  readonly details: ErrorDetails | undefined

  constructor(code: ErrorCode, message?: string, details?: ErrorDetails, options?: { cause?: unknown }) {
    super(message ?? ERROR_MESSAGES[code], options)
    this.name = 'AppError'
    this.code = code
    this.details = details
  }

  get httpStatus(): number {
    return ERROR_HTTP_STATUS[this.code]
  }

  /** true, wenn derselbe Aufruf unverändert noch einmal versucht werden darf */
  get retryable(): boolean {
    return this.code === 'LOCK_TIMEOUT'
  }

  toPayload(): ErrorPayload {
    return this.details
      ? { code: this.code, message: this.message, details: this.details }
      : { code: this.code, message: this.message }
  }

  toJSON(): ErrorPayload {
    return this.toPayload()
  }
}

export const isAppError = (value: unknown): value is AppError => value instanceof AppError

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value)
}

// ---- Kurzformen -------------------------------------------------------------

export const validation = (message?: string, fields?: FieldIssue[]): AppError =>
  new AppError('VALIDATION', message, fields ? { fields } : undefined)

export const unauthenticated = (message?: string): AppError => new AppError('UNAUTHENTICATED', message)

export const forbidden = (message?: string, details?: ErrorDetails): AppError =>
  new AppError('FORBIDDEN', message, details)

/** `notFound('Artikel', id)` → „Artikel wurde nicht gefunden.“ */
export const notFound = (label?: string, id?: string): AppError =>
  new AppError(
    'NOT_FOUND',
    label ? `${label} wurde nicht gefunden.` : undefined,
    label ? { entity: { type: label, id } } : undefined,
  )

export const conflict = (message?: string, details?: ErrorDetails): AppError =>
  new AppError('CONFLICT', message, details)

/** Fachregel verletzt – immer mit einem konkreten Satz („Der Bestand reicht nicht aus.“). */
export const businessRule = (message: string, details?: ErrorDetails): AppError =>
  new AppError('BUSINESS_RULE', message, details)

export const limitExceeded = (message?: string, details?: ErrorDetails): AppError =>
  new AppError('LIMIT_EXCEEDED', message, details)

export const featureDisabled = (featureLabel?: string): AppError =>
  new AppError(
    'FEATURE_DISABLED',
    featureLabel ? `Die Funktion „${featureLabel}“ ist für Ihren Betrieb nicht aktiviert.` : undefined,
  )

export const confirmationRequired = (message?: string, details?: ErrorDetails): AppError =>
  new AppError('CONFIRMATION_REQUIRED', message, details)

export const internal = (cause?: unknown): AppError => new AppError('INTERNAL', undefined, undefined, { cause })

// ---- Umwandlung -------------------------------------------------------------

interface ZodLikeIssue {
  path: readonly PropertyKey[]
  message: string
}

/** Aus den Issues eines ZodError (Zod 4: `error.issues`) wird ein VALIDATION-Fehler mit Feldliste. */
export function fromZodIssues(issues: readonly ZodLikeIssue[], message?: string): AppError {
  const fields = issues.map((issue) => ({ path: issue.path.map(String).join('.'), message: issue.message }))
  return validation(message, fields)
}

/** Beliebigen Fehler in einen AppError überführen; Unbekanntes wird INTERNAL (ohne Interna im Text). */
export function toAppError(error: unknown): AppError {
  if (isAppError(error)) return error
  return internal(error)
}

/** Client-Seite: Fehlerantwort des Servers wieder zu einem AppError machen. */
export function fromErrorPayload(payload: unknown): AppError {
  if (payload && typeof payload === 'object') {
    const p = payload as { code?: unknown; message?: unknown; details?: unknown }
    if (isErrorCode(p.code)) {
      return new AppError(
        p.code,
        typeof p.message === 'string' ? p.message : undefined,
        p.details && typeof p.details === 'object' ? (p.details as ErrorDetails) : undefined,
      )
    }
  }
  return new AppError('INTERNAL')
}
